/**
 * Pupillary distance, exactly.
 *
 * PD is written in tenths of a millimetre — 31.7, 63.5 — and a tenth is not exactly representable
 * in binary floating point: `31.7 + 32.6` evaluates to `64.30000000000001`. Small, until it is a
 * coherence check that refuses a legitimate prescription or accepts an incoherent one.
 *
 * So the same discipline money follows: a decimal string on the wire, an integer internally, and
 * arithmetic only ever on the integer. The unit here is a tenth of a millimetre, which is the
 * precision an optician actually writes and the precision the database column holds.
 */

/** Tenths of a millimetre. 317 is 31.7 mm. */
export type PdTenths = number;

const PD_PATTERN = /^-?\d{1,3}(?:\.\d)?$/;

/** A monocular PD outside this is a typo, not a face. */
export const MONOCULAR_PD_RANGE_TENTHS = { min: 150, max: 450 } as const;
/** A binocular PD outside this is a typo, not a face. */
export const TOTAL_PD_RANGE_TENTHS = { min: 300, max: 900 } as const;

/**
 * Reads a decimal string as tenths.
 *
 * Returns null rather than throwing so a DTO can report the field, and refuses anything with more
 * than one decimal place instead of silently rounding: 31.75 is not a PD anyone measured, and
 * quietly turning it into 31.8 would hide a mistake in whatever produced it.
 */
export function parsePdToTenths(value: string): PdTenths | null {
  const trimmed = value.trim();
  if (!PD_PATTERN.test(trimmed)) return null;

  const negative = trimmed.startsWith('-');
  const digits = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = '0'] = digits.split('.');

  const tenths = Number(whole) * 10 + Number(fraction);
  return negative ? -tenths : tenths;
}

/** Writes tenths back as the decimal string the wire and the screen use. */
export function formatPdTenths(tenths: PdTenths): string {
  const negative = tenths < 0;
  const absolute = Math.abs(tenths);

  return `${negative ? '-' : ''}${Math.floor(absolute / 10)}.${absolute % 10}`;
}

/** For the database column, which is `Decimal(4,1)` and takes the same decimal string. */
export function pdTenthsToDecimalString(tenths: PdTenths | null | undefined): string | null {
  return tenths === null || tenths === undefined ? null : formatPdTenths(tenths);
}

/** Reads a `Decimal(4,1)` column — Prisma hands it back as a Decimal or a string — into tenths. */
export function pdFromDatabase(value: { toString(): string } | null | undefined): PdTenths | null {
  if (value === null || value === undefined) return null;
  return parsePdToTenths(value.toString());
}

export interface PdTriple {
  rightTenths: PdTenths | null;
  leftTenths: PdTenths | null;
  totalTenths: PdTenths | null;
}

export interface PdProblem {
  field: 'rightPdMm' | 'leftPdMm' | 'totalPdMm';
  message: string;
}

/**
 * Whether a set of PD values could describe one face.
 *
 * Every field is optional on purpose. A ruler across the bridge gives a total and no monoculars; a
 * camera may find monoculars and derive the total; a lensmeter reading gives one eye. Demanding all
 * three would refuse measurements that are perfectly usable, so the rule only fires when there is
 * something to compare.
 *
 * The tolerance is one tenth, because opticians round the total independently of the monoculars and
 * a 0.1 disagreement is a rounding convention rather than a contradiction. Anything larger means
 * the lab would be told two different things.
 */
export function checkPdCoherence(triple: PdTriple): PdProblem[] {
  const problems: PdProblem[] = [];

  const inRange = (
    tenths: PdTenths | null,
    range: { min: number; max: number },
    field: PdProblem['field'],
  ) => {
    if (tenths === null) return;
    if (tenths < range.min || tenths > range.max) {
      problems.push({
        field,
        message: `must be between ${formatPdTenths(range.min)} and ${formatPdTenths(range.max)} mm`,
      });
    }
  };

  inRange(triple.rightTenths, MONOCULAR_PD_RANGE_TENTHS, 'rightPdMm');
  inRange(triple.leftTenths, MONOCULAR_PD_RANGE_TENTHS, 'leftPdMm');
  inRange(triple.totalTenths, TOTAL_PD_RANGE_TENTHS, 'totalPdMm');

  if (problems.length > 0) return problems;

  const { rightTenths, leftTenths, totalTenths } = triple;
  if (rightTenths === null || leftTenths === null || totalTenths === null) return problems;

  // Integer arithmetic throughout: 317 + 326 = 643, and 643 is either 643 or it is not.
  const sum = rightTenths + leftTenths;
  if (Math.abs(sum - totalTenths) > 1) {
    problems.push({
      field: 'totalPdMm',
      message: `the monocular PDs add up to ${formatPdTenths(sum)} mm, but the total says ${formatPdTenths(totalTenths)} mm`,
    });
  }

  return problems;
}

/** True when there is at least one PD figure to record. */
export function hasAnyPd(triple: PdTriple): boolean {
  return triple.rightTenths !== null || triple.leftTenths !== null || triple.totalTenths !== null;
}

/**
 * Fills in the total when both monoculars are known and it was not given.
 *
 * Only in that direction. Splitting a total into two monoculars would invent a symmetry nobody
 * measured — faces are not symmetrical, which is why monocular PD exists at all.
 */
export function deriveTotalPd(triple: PdTriple): PdTriple {
  if (triple.totalTenths !== null) return triple;
  if (triple.rightTenths === null || triple.leftTenths === null) return triple;

  return { ...triple, totalTenths: triple.rightTenths + triple.leftTenths };
}
